/**
 * Call the provider-neutral Vault connector routes.
 *
 * Feed listing, scheduling, synchronization, and deletion have one wire shape
 * for every provider. Provider creation and specialized operations stay beside
 * their provider so this module never grows Kuali- or Box-specific arguments.
 */
import type { NodeApiClient } from '@/app-shell/api/node-api-client'
import type {
  ConnectorAnalysisAction,
  ConnectorAnalysisActionResponse,
  ConnectorAnalysisStatusList,
  ConnectorDocumentDisposition,
  ConnectorDocumentMappingCatalog,
  ConnectorFeed,
  ConnectorManagedRecord,
  ConnectorPullCatalog,
  ConnectorSchedule,
  ConnectorSelectionPreview,
} from './types'

export const connectorPath = (orgId: string, vaultId: string) =>
  `/org/${encodeURIComponent(orgId)}/vaults/${encodeURIComponent(vaultId)}`

export interface ConnectorFeedList {
  feeds: ConnectorFeed[]
  // Server-derived capability map keyed by provider. Query controls enable
  // from this answer, never from client role flags.
  canDefineQueryByProvider: Record<string, boolean>
  // Adapter-declared authority per provider: 'workspace_admin' (OAuth) or
  // 'connector_admin' (integration credentials requiring a grant).
  queryAuthorityByProvider: Record<string, string>
  // Server-derived authority for deleting every workspace record from a feed.
  canResetWorkspaceByProvider: Record<string, boolean>
}

export const listConnectorFeeds = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
) => (await api.get<ConnectorFeedList>(
  `${connectorPath(orgId, vaultId)}/feeds`,
)).data

export const getConnectorDocumentMappingCatalog = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
) => (await api.get<ConnectorDocumentMappingCatalog>(
  `${connectorPath(orgId, vaultId)}/feeds/document-mapping`,
)).data

export const getConnectorPullCatalog = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
) => (await api.get<ConnectorPullCatalog>(
  `${connectorPath(orgId, vaultId)}/feeds/pull-catalog`,
)).data

export const pullConnectorRecords = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  feedId: string,
  recordTypes: string[],
  identifiers: string[],
) => (await api.post<{ jobId: string; status: string }>(
  `${connectorPath(orgId, vaultId)}/feeds/${encodeURIComponent(feedId)}/pull`,
  { recordTypes, identifiers },
)).data

export const listConnectorManagedRecords = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  feedId: string,
) => (await api.get<{ records: ConnectorManagedRecord[] }>(
  `${connectorPath(orgId, vaultId)}/feeds/${encodeURIComponent(feedId)}/managed-records`,
)).data.records

export const syncManagedConnectorRecord = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  feedId: string,
  recordKey: string,
) => (await api.post<{ jobId: string; status: string }>(
  `${connectorPath(orgId, vaultId)}/feeds/${encodeURIComponent(feedId)}/managed-records/${encodeURIComponent(recordKey)}/sync`,
)).data

export const unsyncConnectorRecord = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  feedId: string,
  recordKey: string,
) => (await api.delete<{ jobId: string; status: string }>(
  `${connectorPath(orgId, vaultId)}/feeds/${encodeURIComponent(feedId)}/managed-records/${encodeURIComponent(recordKey)}`,
)).data

export const previewConnectorFeed = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  request: object,
) => (await api.post<ConnectorSelectionPreview>(
  `${connectorPath(orgId, vaultId)}/feeds/preview`,
  request,
)).data

export const updateConnectorFeedSchedule = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  feedId: string,
  schedule: ConnectorSchedule,
) => (await api.patch<ConnectorFeed>(
  `${connectorPath(orgId, vaultId)}/feeds/${encodeURIComponent(feedId)}/schedule`,
  { schedule },
)).data

export const syncConnectorFeed = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  feedId: string,
) => (await api.post<{ jobId: string; status: string }>(
  `${connectorPath(orgId, vaultId)}/feeds/${encodeURIComponent(feedId)}/sync`,
)).data

export const refreshSyncedConnectorFeed = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  feedId: string,
) => (await api.post<{ jobId: string; status: string }>(
  `${connectorPath(orgId, vaultId)}/feeds/${encodeURIComponent(feedId)}/refresh-synced`,
)).data

export const disconnectConnectorFeed = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  feedId: string,
  documentDisposition: ConnectorDocumentDisposition,
) => (await api.post<{ jobId: string; status: string }>(
  `${connectorPath(orgId, vaultId)}/feeds/${encodeURIComponent(feedId)}/disconnect`,
  { documentDisposition },
)).data

export const listConnectorAnalyses = async (
  api: NodeApiClient,
  orgId: string,
  workspaceId: string,
) => (await api.get<ConnectorAnalysisStatusList>(
  `/org/${encodeURIComponent(orgId)}/ws/${encodeURIComponent(workspaceId)}/contract-review/connector-analyses`,
)).data.analyses

export const updateConnectorAnalysis = async (
  api: NodeApiClient,
  orgId: string,
  workspaceId: string,
  feedId: string,
  action: ConnectorAnalysisAction,
) => (await api.post<ConnectorAnalysisActionResponse>(
  `/org/${encodeURIComponent(orgId)}/ws/${encodeURIComponent(workspaceId)}/contract-review/connector-analyses/${encodeURIComponent(feedId)}`,
  { action },
)).data
